/**
 * Objekt-palett i Bygg-fanen: de fem objekttypene (søyle, port, stasjon,
 * lading, sonemarkør) som knapper. Klikk starter plasseringsmodus i 2D —
 * klikk i kartet plasserer objektet, Esc (eller nytt klikk på samme type)
 * avslutter.
 */

import React from 'react';
import {
  Columns3,
  DoorOpen,
  PackageCheck,
  BatteryCharging,
  MapPin,
} from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WarehouseObjectType } from '../types/warehouseLayout';
import { OBJECT_TYPES, objectTypeLabel } from '../utils/objectTools';

const OBJECT_TYPE_ICONS: Record<WarehouseObjectType, React.ReactNode> = {
  pillar: <Columns3 className="w-4 h-4" />,
  door: <DoorOpen className="w-4 h-4" />,
  station: <PackageCheck className="w-4 h-4" />,
  charging: <BatteryCharging className="w-4 h-4" />,
  zone_marker: <MapPin className="w-4 h-4" />,
};

const ObjectPalette: React.FC = () => {
  const { state, dispatch } = useLayoutEditor();
  const { t } = useTranslation();
  const activeType = state.objectPlacement?.type ?? null;

  const handleClick = (type: WarehouseObjectType) => {
    if (activeType === type) {
      dispatch({ type: 'CANCEL_OBJECT_PLACEMENT' });
    } else {
      dispatch({ type: 'START_OBJECT_PLACEMENT', objectType: type });
    }
  };

  return (
    <div className="px-3 pb-3">
      <p className="text-xs text-gray-400 py-1">
        {t(
          'manager.modules.warehouseLayout.objects.paletteHint',
          {},
          'Velg en type og klikk i 2D-kartet for å plassere.'
        )}
      </p>
      <div className="grid grid-cols-2 gap-1.5">
        {OBJECT_TYPES.map(type => {
          const active = activeType === type;
          return (
            <button
              key={type}
              onClick={() => handleClick(type)}
              aria-pressed={active}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                active
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              title={t(
                'manager.modules.warehouseLayout.objects.placeTooltip',
                { '0': objectTypeLabel(t, type) },
                `Plasser ${objectTypeLabel(t, type).toLowerCase()} i 2D-kartet`
              )}
            >
              {OBJECT_TYPE_ICONS[type]}
              <span className="truncate">{objectTypeLabel(t, type)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ObjectPalette;
